import { FC, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSecurityContext } from '../../security/hook/useSecurityContext';
import usePermission from '../../security/hook/usePermission';
import {
    Drawer,
    List,
    ListItem,
    ListItemText,
    Box,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    Store as StoreIcon,
} from '@mui/icons-material';
import { arrayIsEmpty } from '../../shared/util/arrayUtils';
import { leftMenuItems, MenuItem } from './menuItems';
import { MenuHeader } from './MenuHeader';
import { MenuItemWithSubmenu } from './MenuItemWithSubmenu';
import { SimpleMenuItem } from './SimpleMenuItem';
import { SUBMENU_CONFIG } from './submenuConfig';

import './leftmenu.css';

type LeftMenuProps = {
    mobileOpen: boolean;
    onDrawerToggle: () => void;
    drawerWidth: number;
};


const LeftMenu: FC<LeftMenuProps> = ({ mobileOpen, onDrawerToggle, drawerWidth }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { user } = useSecurityContext();
    const { hasPermission } = usePermission();

    const [activeItem, setActiveItem] = useState<string | null>(null);
    const [isRequestsSubmenuOpen, setIsRequestsSubmenuOpen] = useState(false);
    const [isUsersSubmenuOpen, setIsUsersSubmenuOpen] = useState(false);

    useEffect(() => {
        setActiveItem(location.pathname);
        // Auto-open submenu if we're on a submenu route
        if (location.pathname.includes('/request') || location.pathname.includes('/create-request')) {
            setIsRequestsSubmenuOpen(true);
        }
        if (location.pathname.includes('/user') || location.pathname.includes('/create-user')) {
            setIsUsersSubmenuOpen(true);
        }
    }, [location.pathname]);

    // Filter menu items based on permissions
    const filteredItemsOnPermissions: MenuItem[] | undefined = leftMenuItems?.filter(
        (item: MenuItem) => hasPermission(item.resource, item.requiredAction || 'read')
    );

    // Filter submenu items based on permissions
    const getFilteredSubmenuItems = (menuLabel: string) => {
        const submenuItems = SUBMENU_CONFIG[menuLabel] || [];
        return submenuItems.filter(item => hasPermission(item.resource, item.requiredAction));
    };

    const handleMenuItemClick = (route: string): void => {
        const urlSegments = location.pathname.split('/');
        urlSegments.pop();
        const newUrl: string = urlSegments.join('/') + route;

        navigate(newUrl);
        setActiveItem(newUrl);

        // Close mobile drawer on navigation
        if (isMobile) {
            onDrawerToggle();
        }
    };

    const getMenuIcon = (label: string) => {
        switch (label) {
            case 'Users':
                return <PeopleIcon />;
            case 'Requests':
                return <AssignmentIcon />;
            case 'Store':
                return <StoreIcon />;
            default:
                return null;
        }
    };

    const renderMenuItem = (item: MenuItem) => {
        // Check if item has submenu
        if (item.label === 'Requests') {
            const filteredSubmenu = getFilteredSubmenuItems('Requests');

            // Don't render if no submenu items are accessible
            if (filteredSubmenu.length === 0) {
                return null;
            }

            return (
                <MenuItemWithSubmenu
                    key="requests-menu"
                    label={item.label}
                    icon={getMenuIcon(item.label)}
                    isOpen={isRequestsSubmenuOpen}
                    onToggle={() => setIsRequestsSubmenuOpen(!isRequestsSubmenuOpen)}
                    activeItem={activeItem}
                    onItemClick={handleMenuItemClick}
                    submenuItems={filteredSubmenu}
                />
            );
        }

        if (item.label === 'Users') {
            const filteredSubmenu = getFilteredSubmenuItems('Users');

            // Don't render if no submenu items are accessible
            if (filteredSubmenu.length === 0) {
                return null;
            }

            return (
                <MenuItemWithSubmenu
                    key="users-menu"
                    label={item.label}
                    icon={getMenuIcon(item.label)}
                    isOpen={isUsersSubmenuOpen}
                    onToggle={() => setIsUsersSubmenuOpen(!isUsersSubmenuOpen)}
                    activeItem={activeItem}
                    onItemClick={handleMenuItemClick}
                    submenuItems={filteredSubmenu}
                />
            );
        }

        const isActive = activeItem === item.route;
        return (
            <SimpleMenuItem
                key={item.route}
                label={item.label}
                icon={getMenuIcon(item.label)}
                isActive={isActive}
                onClick={() => handleMenuItemClick(item.route)}
            />
        );
    };

    const drawerContent = (
        <Box className="iauto-leftmenu-wrapper">
            <MenuHeader title="Navigation" subtitle={user?.user?.role || 'Admin'} />
            <List sx={{ px: 1, py: 2 }}>
                {arrayIsEmpty(filteredItemsOnPermissions) ? (
                    <ListItem>
                        <ListItemText
                            primary="No menu items available"
                            secondary="Contact administrator"
                            sx={{ textAlign: 'center' }}
                        />
                    </ListItem>
                ) : (
                    filteredItemsOnPermissions?.map(renderMenuItem)
                )}
            </List>
        </Box>
    );

    return (
        <>
            {/* Mobile Drawer */}
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={onDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better mobile performance
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            ) : (
                // Desktop Drawer
                <Drawer
                    variant="permanent"
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            top: 64, // Below header
                            height: 'calc(100vh - 64px)',
                        },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

export default LeftMenu;

